import type { FC } from "react";
import { useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { FoodsIcon, FoodsTooltip, iconMapping } from "@foodpilot/foods";

type MenuIconSelectorProps = {
    value?: string;
    onChange: (iconKey: string) => void;
    label?: string;
};

// Curated list of FoodsIcon keys commonly used in the app
const ICON_KEYS: Array<keyof typeof iconMapping> = [
    "home",
    "corporate",
    "diagnostic",
    "trajectory",
    "products",
    "ingredients",
    "barCode",
    "flour",
    "wheat",
    "packaging",
    "supplier",
    "documentation",
    "scoresIndicators",
    "graph",
    "settings",
    "hamburgerMenu",
    "list",
    "gridView",
    "questionnaires",
    "users",
    "strategy",
    "documentSpace",
    "search",
    "filter",
    "add",
    "edit",
    "delete",
    "save",
    "default",
];

export const MenuIconSelector: FC<MenuIconSelectorProps> = ({ value, onChange, label }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return ICON_KEYS;
        return ICON_KEYS.filter((k) => k.toLowerCase().includes(q));
    }, [query]);

    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2">{label ?? "Icon"}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FoodsIcon
                        size={2}
                        icon={
                            value && Object.prototype.hasOwnProperty.call(iconMapping, value) ?
                                (value as keyof typeof iconMapping)
                            :   "default"
                        }
                    />
                </Box>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {value || "default"}
                </Typography>
                <Button variant="outlined" onClick={() => setOpen(true)}>
                    Choose icon
                </Button>
            </Stack>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Select an icon</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search icon..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <Grid container spacing={1}>
                        {filtered.map((key) => (
                            <Grid item xs={2} sm={1.5} md={1} key={key}>
                                <FoodsTooltip title={key} arrow>
                                    <IconButton
                                        onClick={() => {
                                            onChange(key);
                                            setOpen(false);
                                        }}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <FoodsIcon size={2} icon={key as keyof typeof iconMapping} />
                                    </IconButton>
                                </FoodsTooltip>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
